import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Form, message, Select, Checkbox } from 'antd';
import {
  StyledSendMessageDrawer,
  StyledFooter,
  StyledDrawerTitle,
} from './style';
import { Button } from '../../Button';
import { TextArea } from '../../TextArea';
import { Input } from '../../Input';
import useQueryApiClient from '../../../utils/useQueryApiClient';
import { useAuth } from 'hooks/useAuth';
import { Label } from 'ui/Label';

interface DrawerSendMessageProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  context?: {
    type: 'listing' | 'transaction' | 'application';
    listingId?: string;
    transactionId?: number;
    applicationId?: number;
    preselectedRecipients?: number[];
    listingTitle?: string;
    listingIdentifier?: string;
    transactionType?: string;
    invoiceNr?: string;
  };
}

interface RecipientFarm {
  id: number;
  name: string;
  brand?: string;
}

interface FormValues {
  recipient_farm_ids: number[];
  subject: string;
  body: string;
}

const MAX_CHARS = 500;

export const DrawerSendMessage = ({
  open,
  onClose,
  onSuccess,
  context,
}: DrawerSendMessageProps) => {
  const [form] = Form.useForm();
  const intl = useIntl();
  const { user } = useAuth();
  const farmId = user?.farmId ?? user?.farm?.id;
  const [selectAll, setSelectAll] = useState(false);
  const [, setBodyLength] = useState(0);
  const [, setSubjectLength] = useState(0);

  const recipientsParams = context?.listingId
    ? {
        listing_id: context.listingId,
        ...(context.preselectedRecipients?.length === 1
          ? { recipient_farm_id: context.preselectedRecipients[0] }
          : {}),
      }
    : context?.transactionId
      ? { transaction_id: context.transactionId }
      : undefined;

  const {
    data: recipientsData,
    isLoading: isLoadingRecipients,
  } = useQueryApiClient({
    request: {
      url: '/api/v2/messages/recipients',
      data: recipientsParams,
    },
    enabled: open && !!(context?.listingId || context?.transactionId),
    onError: (error) => {
      console.error('Error fetching recipients:', error);
    },
  });

  const recipients: RecipientFarm[] = useMemo(() => {
    if (!recipientsData) return [];

    if (Array.isArray(recipientsData)) {
      return recipientsData;
    }

    const data = recipientsData?.data;
    return Array.isArray(data) ? data : [];
  }, [recipientsData]);

  const { appendData: sendMessage, isLoading: isSending } = useQueryApiClient({
    request: {
      url: '/api/v2/messages',
      method: 'POST',
    },
    onSuccess: (data) => {
      const count = data?.messages_sent || 0;
      message.success(
        intl.formatMessage({ id: 'messages.send_success' }, { count })
      );
      form.resetFields();
      setSelectAll(false);
      setBodyLength(0);
      setSubjectLength(0);
      onClose();
      onSuccess?.();
    },
    onError: () => {
      message.error(intl.formatMessage({ id: 'messages.send_error' }));
    },
  });

  useEffect(() => {
    if (open && recipients.length > 0) {
      if (context?.preselectedRecipients?.length) {
        form.setFieldValue('recipient_farm_ids', context.preselectedRecipients);
      } else if (recipients.length === 1) {
        form.setFieldValue('recipient_farm_ids', [recipients[0].id]);
      }
    }
  }, [
    open,
    context?.preselectedRecipients,
    recipients,
    form,
  ]);

  useEffect(() => {
    if (!open || !farmId || isLoadingRecipients) return;
    if (
      recipients.length === 1 &&
      Number(recipients[0].id) === Number(farmId)
    ) {
      message.warning(intl.formatMessage({ id: 'chat.cannot_send_to_self' }));
      onClose();
    }
  }, [open, farmId, isLoadingRecipients, recipients, onClose, intl]);

  useEffect(() => {
    if (!open || !context) return;

    let subject = '';
    if (context.listingTitle && context.listingIdentifier) {
      subject = `${context.listingTitle}, ${context.listingIdentifier}`;
    } else if (
      context.transactionType !== undefined &&
      context.invoiceNr !== undefined
    ) {
      const rawType = context.transactionType?.toLowerCase();
      const type =
        rawType === 'order'
          ? intl.formatMessage({ id: 'transactions.order_transaction' })
          : rawType
            ? intl.formatMessage({
                id: `transactions.mother_${context.transactionType}_transaction`,
                defaultMessage: context.transactionType,
              })
            : intl.formatMessage({ id: 'transactions.emarket_transaction' });
      const transactionNrLabel = intl.formatMessage({
        id: 'transactions.transaction_nr',
      });
      subject = `${type}, ${transactionNrLabel} ${context.invoiceNr}`;
    }

    if (subject) {
      form.setFieldValue('subject', subject);
      setSubjectLength(subject.length);
    }
  }, [open, context, form, intl]);

  const handleSelectAllChange = (checked: boolean) => {
    setSelectAll(checked);
    if (checked) {
      const allIds = recipients.map((r) => r.id);
      form.setFieldValue('recipient_farm_ids', allIds);
    } else {
      form.setFieldValue('recipient_farm_ids', []);
    }
  };

  const handleSubmit = (values: FormValues) => {
    if (
      farmId &&
      values.recipient_farm_ids?.some((id) => Number(id) === Number(farmId))
    ) {
      message.warning(intl.formatMessage({ id: 'chat.cannot_send_to_self' }));
      onClose();
      return;
    }

    const payload: Record<string, any> = {
      recipient_farm_ids: values.recipient_farm_ids,
      subject: values.subject,
      body: values.body,
    };

    if (context?.listingId) {
      payload.listing_id = context.listingId;
    }
    if (context?.applicationId) {
      payload.listing_application_id = context.applicationId;
    }
    if (context?.transactionId) {
      payload.order_transaction_id = context.transactionId;
    }

    sendMessage(payload);
  };

  const handleClose = () => {
    form.resetFields();
    setSelectAll(false);
    setBodyLength(0);
    setSubjectLength(0);
    onClose();
  };

  const handleBodyChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setBodyLength(e.target.value.length);
  };

  const handleSubjectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSubjectLength(e.target.value.length);
  };

  return (
    <StyledSendMessageDrawer
      open={open}
      onClose={handleClose}
      zIndex={1200}
      title={
        <StyledDrawerTitle>
          <Label
            label={intl.formatMessage({ id: 'messages.send_message_title' })}
            title
          />
        </StyledDrawerTitle>
      }
      width={1000}
      destroyOnClose
      className="styled-drawer"
      footer={
        <StyledFooter>
          <Button
            type="default"
            onClick={handleClose}
            disabled={isSending}
            label={intl.formatMessage({ id: 'general.cancel' })}
          />
          <Button
            type="primary"
            onClick={() => form.submit()}
            loading={isSending}
            label={intl.formatMessage({ id: 'messages.send_button' })}
          />
        </StyledFooter>
      }
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <div className="selection-section">
          <Form.Item
            name="recipient_farm_ids"
            label={intl.formatMessage({ id: 'messages.recipients' })}
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: 'messages.recipients_required',
                }),
              },
            ]}
          >
            <Select
              mode="multiple"
              placeholder={intl.formatMessage({
                id: 'messages.select_recipients',
              })}
              loading={isLoadingRecipients}
              options={recipients.map((r) => ({
                value: r.id,
                label: r.brand || r.name,
              }))}
              filterOption={(input, option) =>
                (option?.label as string)
                  ?.toLowerCase()
                  .includes(input.toLowerCase())
              }
              showSearch
              allowClear
            />
          </Form.Item>
          <Label
            className="info-text"
            label={intl.formatMessage({ id: 'messages.info_text' })}
            title
          />

          {context?.listingId && recipients.length > 0 && (
            <Checkbox
              className="select-all-checkbox"
              checked={selectAll}
              onChange={(e) => handleSelectAllChange(e.target.checked)}
            >
              {intl.formatMessage({ id: 'messages.select_all_applicants' })}
            </Checkbox>
          )}
        </div>

        <Input
          name="subject"
          label={intl.formatMessage({ id: 'messages.subject' })}
          placeholder={intl.formatMessage({
            id: 'messages.subject_placeholder',
          })}
          validations={['required']}
          onChange={handleSubjectChange}
        />
        <TextArea
          className="body-textarea"
          name="body"
          placeholder={intl.formatMessage({
            id: 'messages.body_placeholder',
          })}
          label={intl.formatMessage({ id: 'messages.body' })}
          maxLength={MAX_CHARS}
          validations={['required']}
          onChange={handleBodyChange}
          showCount={true}
        />
      </Form>
    </StyledSendMessageDrawer>
  );
};

export default DrawerSendMessage;
